import { useEffect } from 'react';
import { generatePath, useLoaderData, useLocation, useNavigate, useRouteLoaderData } from 'react-router';
import { paths } from '../../../../../common/constants/pathVariables.js';
import { SubscriptionCategory } from '../../../../../common/enums.js';
import { createOnlineOrder } from '../../../../../common/fetch.js';
import { t, yourOrderHasBeenTransferredForProcessingMsg } from '../../../../../common/i18n/index.js';
import { getSubscriptionUrlId } from '../../../../../common/utils/subscriptionUtils.js';
import { AttachRing } from '../../../../../components/AttachRing/AttachRing.js';
import { AddOnPurpose, DeliveryType } from '../../../../../generated/api/models.js';
import { addNotification } from '../../../notificationStore.js';
import type { MobileSubAttachRingLoaderData, MobileSubscriptionLoaderResponse } from '../../../../../common/loaders';
import type { ConfiguredOffer } from '../../../../../common/types/commercialProduct.js';
import type { DeliveryAddress, OnlineOrder } from '../../../../../generated/api/models.js';
import type {
  AssociationRecord,
  DependencyRecord,
} from '../../../../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';

export const MobileSubAttachRingPath = () => {
  const navigate = useNavigate();
  const { addOnRules, companyInfo, hasPendingActions, onlineModel, subscription, numberRanges } =
    useLoaderData() as MobileSubAttachRingLoaderData;
  const { search } = useLocation();
  const voiceSub = useRouteLoaderData('voiceSub') as MobileSubscriptionLoaderResponse;
  const addOnAssociations = (voiceSub?.addOnRules?.associations?.[0] as AssociationRecord[]) ?? [];

  useEffect(() => {
    if (hasPendingActions) {
      navigate(
        generatePath(paths.PS_MOBILE_SUBSCRIPTION, { subscriptionId: getSubscriptionUrlId(subscription) }) + search,
        {
          replace: true,
        }
      );
    }
  }, [navigate, search, subscription, hasPendingActions]);

  const handleSubmitRingOrder = async (
    offer: ConfiguredOffer,
    deliveryAddress: DeliveryAddress,
    billingAccountId: string
  ) => {
    const product = offer.selectedCommercialProducts[0];
    const addOns =
      product.commercialProduct.addOnAssociations
        ?.filter(assoc => assoc.addOnPurpose === AddOnPurpose.BY_DEFAULT)
        .map(assoc => ({
          addOnAssociationCode: assoc.addOnAssociationCode,
          addOnCode: assoc.addOnCode,
        })) ?? [];

    const orderItem = {
      addOns,
      commercialProductCode: product.commercialProduct.commercialProductCode,
      commercialProductName: product.commercialProduct.commercialProductName,
      offerCode: offer.offer.offerCode,
      orderItemQuantity: 1,
      orderItemUnits: [
        {
          deliveryType: DeliveryType.NONE,
          purposeOfUse: product.purposeOfUseOrContact.purposeOfUse,
          costCenter: product.purposeOfUseOrContact.costCenter ?? '',
          subscriptionReference: product.purposeOfUseOrContact.employeeNumber,
          userContact: product.purposeOfUseOrContact.contactId,
          mobilePbxServiceLevelDetails: product.mobilePbxServiceLevelDetails,
        },
      ],
    };

    const onlineOrder: Partial<OnlineOrder> = {
      billingAccountId,
      deliveryAddress,
      orderItems: [orderItem],
      totalFixedTermCharge: 0,
      totalMonthlyRecurringCharge: product.commercialProduct.monthlyRecurringCharge ?? 0,
      totalOneTimeCharge: 0,
      totalOneTimeChargeVat: 0,
    };

    await createOnlineOrder(onlineOrder);
    addNotification(t.FDSO(yourOrderHasBeenTransferredForProcessingMsg));
    navigate(
      generatePath(paths.PS_MOBILE_SUBSCRIPTION, { subscriptionId: getSubscriptionUrlId(subscription) }) + search
    );
  };

  return (
    <AttachRing
      category={SubscriptionCategory.VOICE}
      companyInfo={companyInfo}
      numberRanges={numberRanges}
      onClickSubscription={(subId: string) =>
        navigate(generatePath(paths.PS_MOBILE_SUBSCRIPTION, { subscriptionId: subId }) + search)
      }
      onSubmitRingOrder={handleSubmitRingOrder}
      subscription={subscription}
      onlineModels={[onlineModel]}
      associations={(addOnRules?.associations?.[0] as AssociationRecord[]) || []}
      dependencies={(addOnRules?.dependencies?.[0] as DependencyRecord[]) || []}
      addOnAssociations={addOnAssociations}
    />
  );
};
